import React, { createContext, useContext, useEffect, useState } from "react";
import { useAuth } from "@/lib/auth-context";

export const COUNTRIES = {
  GH: { code: "GH", name: "Ghana", flag: "🇬🇭", currency: "GHS", currencySymbol: "GH₵", exchangeRate: 15.5, dialCode: "+233" },
  NG: { code: "NG", name: "Nigeria", flag: "🇳🇬", currency: "NGN", currencySymbol: "₦", exchangeRate: 1600, dialCode: "+234" },
};

const CountryContext = createContext(undefined);

export function CountryProvider({ children }) {
  const { user, isCustomer } = useAuth();
  const [code, setCode] = useState(() => localStorage.getItem("cravve-country") || "GH");
  const [locked, setLocked] = useState(() => localStorage.getItem("cravve-country-locked") === "1");

  // Lock country to whatever the signed-in customer registered with
  useEffect(() => {
    if (isCustomer && user?.country && (user.country === "GH" || user.country === "NG")) {
      setCode(user.country);
      localStorage.setItem("cravve-country", user.country);
      if (!locked) {
        setLocked(true);
        localStorage.setItem("cravve-country-locked", "1");
      }
    }
  }, [isCustomer, user?.country, locked]);

  useEffect(() => {
    localStorage.setItem("cravve-country", code);
  }, [code]);

  const setCountry = (next) => {
    if (locked) return;            // hard-block any switching after signup
    if (next !== "GH" && next !== "NG") return;
    setCode(next);
  };

  // Used by signup flow to permanently lock the country.
  const lockToCountry = (next) => {
    if (next !== "GH" && next !== "NG") return;
    setCode(next);
    setLocked(true);
    localStorage.setItem("cravve-country", next);
    localStorage.setItem("cravve-country-locked", "1");
  };

  const country = COUNTRIES[code] || COUNTRIES.GH;

  const formatPrice = (usd) => {
    const local = (usd || 0) * country.exchangeRate;
    return `${country.currencySymbol}${local.toLocaleString("en", { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
  };

  return (
    <CountryContext.Provider value={{ country, setCountry, lockToCountry, locked, formatPrice }}>
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error("useCountry must be used within CountryProvider");
  return ctx;
}
